import $ from 'jquery';
import { formDataCompare, formDataDiff } from '../../util/data.js';

/**
 * @param {JQuery} $elem
 */
const getHandlerNamespace = ($elem) => {
	return `widget-change-handler-${$elem.attr('id')}`;
};

/**
 * @param {JQuery} $widgets
 */
export const processFormChangeWidgets = ($widgets) => {

	for (const widget of $widgets) {

		const $widget = $(widget);
		const data = $widget.data('change-handler') || {};
		const subWidgetsWithHandlers = [];

		// Auto map to 'true'
		if (data.checked) {
			const wd = Array.isArray(data.checked) ? data.checked : (data.checked ? [data.checked] : []);
			if (!data.values) {
				data.values = {};
			}
			delete data.checked;
			data.values['true'] = wd;
		}

		// Auto map to 'false'
		if (data.unchecked) {
			const wd = Array.isArray(data.unchecked) ? data.unchecked : (data.unchecked ? [data.unchecked] : []);
			if (!data.values) {
				data.values = {};
			}
			delete data.unchecked;
			data.values['false'] = wd;
		}

		if (data.values) {
			for (let ids of Object.values(data.values)) {
				if (ids) {
					if (!Array.isArray(ids)) {
						ids = [ids];
					}

					// Look for widgets that already have change-handlers inside our collection
					for (const id of ids) {
						// eslint-disable-next-line unicorn/prefer-array-some
						if ($widgets.filter(`#${id}`).length > 0) {
							subWidgetsWithHandlers.push(id);
						}
					}
				}
			}
		}

		// Add sub widgets
		data.subWidgetsWithHandlers = subWidgetsWithHandlers;
	}

	return $widgets;
};

/**
 * @param {HTMLElement} element
 */
export const handleFormChangeWidgets = (element) => {

	const $element = $(element);
	const {
		isMultiChoice,
		changeHandler: changeHandlerOptions = {},
	} = $element.data();

	const {
		actions = ['disable', 'hide'],
		compound,
		values,
		trigger,
		triggerOnInit = false,
		subWidgetsWithHandlers = [],
	} = changeHandlerOptions;

	/** @type {Set<string>} */
	const widgets = new Set();
	/** @type {{ [key: string]: string; }} */
	const mapValues = {};

	// Got values map
	if (values) {
		for (const [value, ids] of Object.entries(values)) {
			let wd = Array.isArray(ids) ? ids : (ids ? [ids] : []);
			// Keep unique values
			wd = [...new Set(wd)];
			// Build id selectors for each toggle value
			mapValues[value] = wd.map(v => `#${v}`).join(',');

			// Add to the widgets list
			for (const id of wd) {
				widgets.add(`#${id}`);
			}
		}
	}

	/** @type {string} */
	const allSelectors = [...widgets].join(',');

	const isCompound = (compound === true) || (isMultiChoice === true);
	// The trigger is only different if this is a compound widget and it has multiple expanded children (example: radio/checkbox buttons, in this case the trigger is an array with all the children ids)
	// eslint-disable-next-line unicorn/consistent-destructuring
	const widgetsSelector = Array.isArray(trigger) ? changeHandlerOptions.trigger.map(v => `#${v}`).join(',') : null;
	const {
		form_row_selector: rowSelector,
		form_section_selector: sectionSelector,
		form_column_selector: columnSelector,
	} = mpro.config.ui;

	// Bind the change handler
	formWidgetsHandleBindChange({
		trigger: $element,
		widgetsSelector,
		rowSelector,
		sectionSelector,
		columnSelector,
		isCompound,
		allWidgetSelectors: allSelectors,
		subAllWidgetSelectors: subWidgetsWithHandlers.map(v => `#${v}`).join(','),
		mapValuesToWidgetSelectors: mapValues,
		actions,
		triggerOnInit,
	});
};

/**
 * @param {BindChangeOptions} opt
 */
export const formWidgetsHandleBindChange = (opt) => {

	const {
		trigger,
		widgetsSelector,
		rowSelector,
		sectionSelector,
		columnSelector,
		allWidgetSelectors,
		subAllWidgetSelectors,
		mapValuesToWidgetSelectors,
		// subMapValuesToWidgetSelectors,
		isCompound = false,
		triggerOnInit = false, // This can be 'true' only when there is no support from the backed (the form is not pre-rendered accordingly)
		affectParentRows = false, // This is only for the shop v4 form
		actions = [],
		callback,
		controlCallback,
	} = opt;

	// console.log('allWidgetSelectors: %o, subAllWidgetSelectors: %o, mapValuesToWidgetSelectors: %o, triggerOnInit: %o', allWidgetSelectors, subAllWidgetSelectors, mapValuesToWidgetSelectors, triggerOnInit);

	/** @type {JQuery} */
	// @ts-ignore
	const $trigger = $(trigger);
	const $widgets = widgetsSelector ? $(widgetsSelector) : $trigger;
	const handlerNamespace = getHandlerNamespace($trigger);
	const firstData = $widgets.first().data();

	// No trigger on init by default
	firstData.skipTriggerChangeWidget = (triggerOnInit !== true) ? true : false;

	/**
	 * @param {string} widgetSetState
	 */
	const getSelectedWidgets = (widgetSetState) => {

		const hasMapWidgets = (mapValuesToWidgetSelectors && !$.isEmptyObject(mapValuesToWidgetSelectors)) ? true : false;
		const selWidgetsOn = hasMapWidgets ? (mapValuesToWidgetSelectors[widgetSetState] || null) : null;
		let selWidgetsOff;

		// Select only widgets mapped to the state value as 'off' widgets
		if (hasMapWidgets) {
			// Convert selectors to array of widget ids
			const arrWidgetsOff = [];
			const selWidgetsOnArr = selWidgetsOn && selWidgetsOn.replaceAll(/\s+/igu, '').split(',') || [];

			// Intersect off values with on values so it doesn't conflict
			for (const widgetValue in mapValuesToWidgetSelectors) {
				if (Object.hasOwn(mapValuesToWidgetSelectors, widgetValue)) {
					const widgetSelectors = mapValuesToWidgetSelectors[widgetValue];

					if ((widgetValue !== widgetSetState) && widgetSelectors) {
						const widgetSelectorsArr = widgetSelectors.replaceAll(/\s+/igu, '').split(',');
						const intersectSelArr = [];

						for (const selector of widgetSelectorsArr) {
							if (selWidgetsOnArr.includes(selector) === false) {
								intersectSelArr.push(selector);
							}
						}

						if (intersectSelArr.length > 0) {
							arrWidgetsOff.push(intersectSelArr.join(','));
						}
					}
				}
			}
			selWidgetsOff = arrWidgetsOff.join(',');
		}
		// All widgets are 'off' widgets (this mode is not actually used - it should be deprecated to avoid confusion)
		else {
			selWidgetsOff = allWidgetSelectors;
		}

		return { selWidgetsOn, selWidgetsOff };
	};

	/**
	 * @type {(this: HTMLElement, e: JQuery.TriggeredEvent, widgetSetState: string, forceAllWidgets?: boolean) => void}
	 */
	const widgetOffHandler = function(e, widgetSetState, forceAllWidgets) {
		const $self = $(this);
		// All widgets are forced to 'off' state
		const selWidgetsOff = (forceAllWidgets === true) ? allWidgetSelectors : getSelectedWidgets(widgetSetState).selWidgetsOff;

		// Have sub widgets
		if (subAllWidgetSelectors) {
			/**
			 * Trigger sub widgets 'off' state
			 * @type {(this: HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|HTMLElement) => void}
			 */
			const subOffHandler = function() {
				const $subMainWidget = $(this);
				const $widgetTargetContainer = $subMainWidget.closest(rowSelector);

				// Checkbox/Radio
				if ($subMainWidget.is('input[type=checkbox],input[type=radio]')) {

					// @ts-ignore
					// console.log('subOffHandler', this.id, this.checked);

					/* Make sure we don't do this multiple times and lose the original prevSelectedWidget value */
					// @ts-ignore
					if (this && !this.jquery && (this.checked === true) && !this.disabled) {
						// @ts-ignore
						$widgetTargetContainer.data('prevSelectedWidget', { jqWidget: $subMainWidget, checked: this.checked });
						$subMainWidget.prop('checked', false).triggerNative('change');
					}
				}
				// Rest of form elements
				else if ($subMainWidget.is('input,textarea,select')) {
					/* Make sure we don't do this multiple times and lose the original prevSelectedWidget value */
					// @ts-ignore
					if (!this.disabled) {
						$widgetTargetContainer.data('prevSelectedWidget', { jqWidget: $subMainWidget, val: $subMainWidget.val() });
						$subMainWidget.val('').triggerNative('change');
					}
				}
				// Compound - recurse
				else {
					$subMainWidget.find('input,select,textarea').each(subOffHandler);
				}
			};

			// Run the sub 'off' handler for all sub-widgets
			$(subAllWidgetSelectors).each(subOffHandler);
		}

		const $allOffWidgets = $(selWidgetsOff);
		const numColWidgets = $allOffWidgets.length;

		// Apply the 'off' handler for each widget
		$allOffWidgets.each(function(widgetColId) {
			// Select required components
			const $target = $(this);
			// Make sure input[type="hidden"] is excluded from row grabbing (it does not usually have a row)
			// Note: this breaks other custom widgets (the date-selector for example, which has a hidden input as the main submit widget)
			// const $targetRow = $target.is('input[type="hidden"]') ? $() : $target.closest(rowSelector);
			const $targetRow = $target.closest(rowSelector);
			const $targetFormElems = $target.is('input,select,textarea')
				? $target
				: $target.find('input,select,textarea').not('.ignore-bind-change');

			// Action: DISABLE
			if (actions.includes('disable')) {

				// Flag the container that its widgets are disabled
				$targetRow.data('disableOnChange', true);

				// Visually disabled
				$targetRow.children().add($target).addClass('disabled');

				// Visually disable all parent rows
				const $targetSameRowParents = (affectParentRows === true) ? $targetRow.parents(rowSelector) : $();
				if ($targetSameRowParents.length > 0) {
					$targetSameRowParents.children().addClass('disabled');
				}

				// Prepare form elements props
				// @ts-ignore
				$targetFormElems.each(function() {
					const $elem = $(this);
					// Save the required state
					// @ts-ignore
					$elem.data('prop_required', this.required);
					// @ts-ignore
					this.required = false;
					// @ts-ignore
					this.disabled = true;

					// Trigger custom disable event on the form element
					$elem.trigger('disable.bind-change-action', {
						disable: true,
						initor: $self,
						target: $target,
						targetElements: $targetFormElems,
						container: $targetRow,
					});
				});

				// Trigger a custom disable event on the row
				$targetRow.trigger('disable.bind-change-action-container', {
					disable: true,
					initor: $self,
					target: $target,
					targetElements: $targetFormElems,
					container: $targetRow,
				});
			}

			// Action: HIDE
			if (actions.includes('hide')) {

				const $targetSameRowParents = (affectParentRows === true) ? $targetRow.parents(rowSelector) : $();
				let $hideContainers = $targetRow;

				if ($targetSameRowParents.length > 0) {
					$hideContainers = $hideContainers.add($targetSameRowParents);
				}

				// Flag the container that its widgets are hidden
				$targetRow.attr('data-hide-on-change', '');

				// See if there are any other rows on the same level
				const $otherRows = $targetRow.siblings(rowSelector).not('[data-hide-on-change]');

				if ($otherRows.length === 0) {
					const $rowSection = $targetRow.closest(sectionSelector);

					if ($rowSection.length > 0) {
						$hideContainers = $hideContainers.add($rowSection);
						$rowSection.attr('data-hide-on-change', '');
					}

					// If there are no other rows in the section, hide the column
					const $otherSections = $rowSection.siblings(sectionSelector).not('[data-hide-on-change]');

					if ($otherSections.length === 0) {
						const $column = $rowSection.closest(columnSelector);

						if ($column.length > 0) {
							$hideContainers = $hideContainers.add($column);
							$column.attr('data-hide-on-change', '');
						}
					}
				}

				// Hide the containers
				$hideContainers.finish().hide();
			}

			// Trigger the callback
			callback && callback('off', $self, $target, $targetRow, $targetFormElems, widgetColId, numColWidgets, e);
		});
	};

	/**
	 * @type {(this: HTMLElement, e: JQuery.TriggeredEvent, widgetSetState: string, forceAllWidgets?: boolean) => void}
	 */
	const widgetOnHandler = function(e, widgetSetState, forceAllWidgets) {

		const $self = $(this);
		// All widgets are forced to 'on' state
		const selWidgetsOn = (forceAllWidgets === true) ? allWidgetSelectors : getSelectedWidgets(widgetSetState).selWidgetsOn;
		const $allOnWidgets = $(selWidgetsOn);
		const numColWidgets = $allOnWidgets.length;

		// Apply the 'on' handler for each widget
		$allOnWidgets.each(function(widgetColId) {
			// Select required components
			const $target = $(this);
			const targetData = $target.data();
			// Note: this breaks other custom widgets (the date-selector for example, which has a hidden input as the main submit widget)
			// const $targetRow = $target.is('input[type="hidden"]') ? $() : $target.closest(rowSelector);
			const $targetRow = $target.closest(rowSelector);
			/** @type {{ jqWidget: JQuery; val?: any; checked?: boolean; }} */
			const prevSelectedWidget = $targetRow.data('prevSelectedWidget');
			const $targetFormElems = $target.is('input,select,textarea')
				? $target
				: $target.find('input,select,textarea').not('.ignore-bind-change');

			// Action: DISABLE
			if (actions.includes('disable')) {

				// Flag the container that its widgets are no longer disabled
				$targetRow.data('disableOnChange', false);

				// Remove visually disabled state
				$targetRow.children().add($target).removeClass('disabled');

				// Remove visually disabled from all parent rows
				const $targetSameRowParents = (affectParentRows === true) ? $targetRow.parents(rowSelector) : $();
				if ($targetSameRowParents.length > 0) {
					$targetSameRowParents.children().removeClass('disabled');
				}

				// Restore form elements props
				// @ts-ignore
				$targetFormElems.each(function() {
					const $elem = $(this);
					if ($elem.data('prop_required') === true) {
						// @ts-ignore
						this.required = true;
					}
					// @ts-ignore
					this.disabled = false;

					// Trigger a custom disable event on the form element
					$elem.trigger('disable.bind-change-action', {
						disable: false,
						initor: $self,
						target: $target,
						targetElements: $targetFormElems,
						container: $targetRow,
					});

					/* Wrong implementation logic */
					/* if (jqThis.is('select')) {
						jqThis.find('option').prop('disabled', false);
						if (!jqThis.is('[multiple]')) {
							var jqSelOption = jqThis.find('option[selected]');
							if (!jqSelOption.length) {
								jqSelOption = jqThis.find('option').first();
							}
							window.setTimeout(function(){
								jqSelOption.attr('selected', 'selected');
							}, 100);
						}
					} */
				});

				// Trigger a custom disable event on the row
				$targetRow.trigger('disable.bind-change-action-container', {
					disable: false,
					initor: $self,
					target: $target,
					targetElements: $targetFormElems,
					container: $targetRow,
				});
			}

			// Action: HIDE
			if (actions.includes('hide')) {

				const $targetSameRowParents = (affectParentRows === true) ? $targetRow.parents(rowSelector) : $();
				let $hideContainers = $targetRow;

				if ($targetSameRowParents.length > 0) {
					$hideContainers = $hideContainers.add($targetSameRowParents);
				}

				// Flag the container that its widgets are not hidden anymore
				$targetRow.removeAttr('data-hide-on-change');

				const $rowSection = $targetRow.closest(sectionSelector).filter('[data-hide-on-change]');

				if ($rowSection.length > 0) {
					$hideContainers = $hideContainers.add($rowSection);
					$rowSection.removeAttr('data-hide-on-change');

					const $column = $rowSection.closest(columnSelector).filter('[data-hide-on-change]');

					if ($column.length > 0) {
						$hideContainers = $hideContainers.add($column);
						$column.removeAttr('data-hide-on-change');
					}
				}

				// Show the containers
				$hideContainers.finish().show();
			}

			// Initial disabled/required states (from backend)
			if (targetData.protoDisabled === true || targetData.protoRequired === true) {
				// Restore elements states
				// @ts-ignore
				$targetFormElems.each(function() {
					// @ts-ignore
					this.disabled = targetData.protoDisabled;
					// @ts-ignore
					this.required = targetData.protoRequired;
				});
			}

			// Restore the prevSelectedWidget
			if (prevSelectedWidget && prevSelectedWidget.jqWidget) {
				// Defer execution to next stack
				window.setTimeout(() => {
					if ('checked' in prevSelectedWidget) {
						prevSelectedWidget.jqWidget.prop('checked', prevSelectedWidget.checked);
					}
					else if ('val' in prevSelectedWidget) {
						prevSelectedWidget.jqWidget.val(prevSelectedWidget.val);
					}
					prevSelectedWidget.jqWidget.triggerNative('change');
					$targetRow.data('prevSelectedWidget', null);
				});
			}

			// Execute the callback
			callback && callback('on', $self, $target, $targetRow, $targetFormElems, widgetColId, numColWidgets, e);
		});

		// Have sub widgets - execute last
		if (subAllWidgetSelectors) {

			/**
			 * Trigger sub widgets 'on/off' state
			 * @type {(this: HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|HTMLElement, namespace?: string) => void}
			 */
			// const subOnOffHandler = function (namespace) {
			// 	const $subMainWidget = $(this);
			// 	// This is the first level, get actual event namespace
			// 	if (!namespace) {
			// 		namespace = getHandlerNamespace($subMainWidget);
			// 	}

			// 	// Form widgets
			// 	if ($subMainWidget.is('input,textarea,select')) {
			// 		/* Make sure we don't do this multiple times and lose the original prevSelectedWidget value */
			// 		// @ts-ignore
			// 		if (!this.disabled) {
			// 			// debugger;
			// 			$subMainWidget.triggerHandler('change.' + namespace);
			// 			// debugger;
			// 		}
			// 	}
			// 	// Compound - recurse
			// 	else {
			// 		$subMainWidget.find('input,select,textarea').each(function () {
			// 			// Pass namespace from first level
			// 			subOnOffHandler.call(this, namespace);
			// 		});
			// 	}
			// };

			// Run the sub 'on/off' handler for all sub-widgets
			// TODO
			// $(subAllWidgetSelectors).each(function () {
			// 	subOnOffHandler.call(this);
			// });
		}
	};

	/**
	 * @type {(this: HTMLElement|null, e?: JQuery.TriggeredEvent) => void}
	 */
	const widgetChangeHandler = function(e) {

		let $self = $(this);
		let that = this;
		let widgetSetState;

		// Checkbox/Radio
		if ((isCompound !== true) && $self.is('input[type=checkbox],input[type=radio]')) {
			// The state is the checked flag
			// @ts-ignore
			widgetSetState = that && !that.jquery && (that.checked).toString() || '';
		}
		// Compound widget
		else {
			// Hmmm, not sure what the purpose of this was
			if ($self.is('input[type=checkbox],input[type=radio]')) {
				// State is the input val
				// @ts-ignore
				if (that.checked) {
					widgetSetState = $self.val();
				}
				// State is nothing (same as the self refs)
				else {
					that = null;
					$self = $();
					widgetSetState = '';
				}
			}
			// Other form element types
			else {
				widgetSetState = $self.val() || [];
			}
		}

		let controlCalled = false;

		// Get state widgets
		const currentStateWidgets = getSelectedWidgets(widgetSetState);

		// We have a control callback defined
		if (allWidgetSelectors && controlCallback) {
			const $onTargets = $(currentStateWidgets.selWidgetsOn);
			const $offTargets = $(currentStateWidgets.selWidgetsOff);
			const $allTargets = $onTargets.add($offTargets);

			// Call the control callback for fine-grained handling
			controlCallback.call(
				that,
				() => {
					controlCalled = true; return widgetOnHandler.call(that, e, widgetSetState, true);
				},
				() => {
					controlCalled = true; return widgetOffHandler.call(that, e, widgetSetState, true);
				},
				$self,
				$allTargets,
				$onTargets,
				$offTargets,
			);
		}

		// Control was not called, we must handle this
		if ((controlCalled === false) && allWidgetSelectors) {
			const { selWidgetsOn } = currentStateWidgets;
			const { selWidgetsOff } = currentStateWidgets;

			if (selWidgetsOff) {
				widgetOffHandler.call(that, e, widgetSetState);
			}
			if (selWidgetsOn) {
				widgetOnHandler.call(that, e, widgetSetState);
			}
		}
	};

	// Bind the handler directly on the form
	$widgets.on(`change.${handlerNamespace}`, widgetChangeHandler);
	$widgets.on(`off-state.${handlerNamespace}`, function(e) {
		widgetOffHandler.call(this, e, 'false');
	});

	// Do not set initial state
	if (!firstData || !firstData.skipTriggerChangeWidget) {
		if (isCompound === true) {
			const jqCheckedWidget = $widgets.filter(':checked');
			if (jqCheckedWidget.length > 0) {
				jqCheckedWidget.triggerHandler(`change.${handlerNamespace}`);
			}
			else {
				widgetChangeHandler.call(null);
			}
		}
		else {
			$widgets.triggerHandler(`change.${handlerNamespace}`);
		}
	}
	// Reset the initial state
	else if (firstData) {
		firstData.skipTriggerChangeWidget = false;
	}
};

/**
 * The submit button should change classes after widgets have been changed
 *
 * @param {JQuery} $forms
 */
// TODO: move this function elsewhere
export const formWidgetsHandleSubmitButton = ($forms) => {

	for (const form of $forms) {

		/** @type {JQuery<HTMLFormElement>} */// @ts-ignore
		const $form = $(form);

		if ($form.is('[data-toggle-submit-button]') === false) {
			continue;
		}

		// Skip if inside overlay
		if (mpro.isInOverlay($form)) {
			continue;
		}

		const $submitButtons = mpro.getFormSubmitButtons($form);

		if ($submitButtons.length > 0) {

			$submitButtons.addClass('opacity-75');

			$form.on('change.submitButtonToggleState', (e) => {
				// Ignore data-ajax-submit changes
				if (e.target.dataset.ajaxSubmit !== undefined) {
					return;
				}
				$submitButtons.removeClass('opacity-75');
				$form.off('change.submitButtonToggleState');
			});
		}
	}
};

/**
 * The overhead should be displayed after widgets have been changed
 *
 * @param {JQuery} $forms
 */
// TODO: move this function elsewhere
export const formHandleOverheadButtons = ($forms) => {

	for (const form of $forms) {

		const $form = $(form);

		// Skip if inside overlay
		if (mpro.isInOverlay($form)) {
			continue;
		}

		const $overheadButtons = $form.find('[data-overhead-buttons]');

		if ($overheadButtons.length === 0) {
			continue;
		}

		$form.on('change.formOverheadButtons', (e) => {
			if (
				// Ignore data-ajax-submit changes
				(e.target.dataset.ajaxSubmit !== undefined) ||
				// Ignore elements that are not submittable
				// @ts-ignore
				(!e.target.name)
			) {
				return;
			}
			$overheadButtons.addClass('show');
			$form.off('change.formOverheadButtons');
		});
	}
};

/**
 * Prevent user to leave the page if there are unsaved changes
 *
 * @param {JQuery<Partial<HTMLFormElement>>} $forms
 * @param {boolean=} skipDelay
 */
// TODO: move this function elsewhere
export const formUnsavedChanges = ($forms, skipDelay) => {

	// It is recommended to use a delay so we avoid unexpected 'change' events or other form mutations (usually triggered by plugins)
	// And also to make sure the submit handler is called last
	const addDelay = 1000;

	for (const form of $forms) {

		const $form = $(form);

		if ($form.is('[data-alert-unsaved-changes]') === false) {
			continue;
		}

		// Skip if inside overlay
		if (mpro.isInOverlay($form)) {
			return;
		}

		const data = $form.data();

		if (data._unsavedChangesTimer) {
			window.clearTimeout(data._unsavedChangesTimer);
			delete data._unsavedChangesTimer;
		}

		if (skipDelay === true) {
			handleFormUnsavedChanges($form);
			continue;
		}

		// Make sure the submit handler is called last
		data._unsavedChangesTimer = window.setTimeout(() => {
			delete data._unsavedChangesTimer;
			handleFormUnsavedChanges($form);
		}, addDelay);

		$form.one('destroy', () => {
			if (data._unsavedChangesTimer) {
				window.clearTimeout(data._unsavedChangesTimer);
				delete data._unsavedChangesTimer;
			}
		});
	}
};

/**
 * Prevent user to leave the page if there are unsaved changes
 *
 * @param {JQuery<Partial<HTMLFormElement>>} $form
 */
const handleFormUnsavedChanges = ($form) => {

	const form = $form[0];
	// const $elements = $form.find('input,select,textarea');

	// @ts-ignore
	const initialState = new FormData(form);
	let hasChanges = false;

	const stopNavigationHandler = (event) => {

		if (!hasChanges) {
			// @ts-ignore
			const currentState = new FormData(form);
			hasChanges = formDataCompare(initialState, currentState) === false;
			// console.debug('Form has unsaved changes:', hasChanges, [...formDataDiff(currentState, initialState)]);
		}

		if (hasChanges) {
			event.preventDefault();
			return (event.returnValue = 'The page has unsaved changes. Are you sure you want to leave?');
		}

		return undefined;
	};

	const handleChanges = () => {
		// @ts-ignore
		const currentState = new FormData(form);
		hasChanges = formDataCompare(initialState, currentState) === false;
		// const diff = formDataDiff(initialState, currentState);
		// console.debug('Form has unsaved changes:', hasChanges, [...diff], [...formDataDiff(currentState, initialState)], e.target);

		if (hasChanges) {
			window.addEventListener('beforeunload', stopNavigationHandler, { capture: true });
			$form.off('change.unsavedChanges', handleChanges);
		}
	};

	// Add the 'beforeunload' event listener only if form changes
	$form.on('change.unsavedChanges', handleChanges);

	$form.find('a[data-cancel-changes], button[data-cancel-changes]').on('click.unsavedChanges', (e) => {
		form.dispatchEvent(new CustomEvent('cancel-changes', { bubbles: false }));
	});

	// TODO: create only one document listener as a delegate (in mpro.addInitDelegate())
	const docNavigationHandler = (e) => {

		const { continueLoad, isHandled } = e.detail;

		if (!hasChanges) {
			// @ts-ignore
			const currentState = new FormData(form);
			hasChanges = formDataCompare(initialState, currentState) === false;
			// console.debug('Form has unsaved changes:', hasChanges, [...formDataDiff(currentState, initialState)]);
		}

		// console.log('willNavigate event triggered', hasChanges);

		if (hasChanges && (isHandled !== true)) {
			mpro.confirm({
				title: 'Unsaved changes',
				message: 'The page has unsaved changes. Are you sure you want to leave?',
				okAction: () => {
					console.log('continueLoad');
					continueLoad();
				},
			});

			e.preventDefault();
		}
		else {
			document.removeEventListener('willNavigate', docNavigationHandler, { capture: true });
		}
	};

	// Add the willNavigate event listener
	document.addEventListener('willNavigate', docNavigationHandler, { capture: true });

	$form
		.one('submit.unsavedChanges', (e) => {
			window.removeEventListener('beforeunload', stopNavigationHandler, { capture: true });
			document.removeEventListener('willNavigate', docNavigationHandler, { capture: true });
		})
		.one('cancel-changes.unsavedChanges', () => {
			window.removeEventListener('beforeunload', stopNavigationHandler, { capture: true });
			document.removeEventListener('willNavigate', docNavigationHandler, { capture: true });
		})
		.one('destroy', () => {
			window.removeEventListener('beforeunload', stopNavigationHandler, { capture: true });
			document.removeEventListener('willNavigate', docNavigationHandler, { capture: true });
		});
};